import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subject, takeUntil} from 'rxjs';
import {IAnalysisMeasurement, IAnalysisMeasurementResult, IIndicatorsPatientConsultation, MeasurementState} from '../../models/measurement';
import {PatientState} from '../../models/patient';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {differenceInMonths} from 'date-fns';
import {cloneDeep, inRange} from 'lodash';
import {es_MX} from '../../locale/es_MX';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {GenderEnum} from '../../models/user';
import {CardiovascularRiskEnum} from 'src/app/enums/record/cardiovascular-risk.enum';

@Component({
  selector: 'app-complex-formulas',
  templateUrl: './complex-formulas.component.html',
  styleUrls: ['./complex-formulas.component.scss'],
  animations: [fadeInAnimation]
})
export class ComplexFormulasComponent implements OnInit, OnDestroy {
  measurements$: Observable<IAnalysisMeasurement> = this.store.select<IAnalysisMeasurement>(MeasurementState.getAnalysisMeasurement);
  indicatorsPatient$: Observable<IIndicatorsPatientConsultation> =
    this.store.select<IIndicatorsPatientConsultation>(PatientState.getPatientSelectedInformation);
  @Input() showCard: boolean;

  private destroy$ = new Subject();

  complexCategories: { name: string; data: IAnalysisMeasurementResult[] }[];
  classificationIMC: {
    prefix: string,
    value: string,
    color: string,
    range: number[],
    operator: string,
  } [];
  indexClassificationSelected;
  colorsCategory: string[];
  es_MX = es_MX;
  classificationCardiovascularRisk: {
    VERY_SLIGHT: string,
    MILD: string,
    MODERATE: string,
    HIGH: string,
    VERY_HIGH: string,
  };

  genderEnum = GenderEnum;
  cardiovascularRiskEnum = CardiovascularRiskEnum;
  imcValue: number;

  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService,
  ) { }

  ngOnInit(): void {
    this.initValues();
    this.listeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.destroy$.complete();
  }



  listeners(): void {
    this.measurements$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async measurements => {
      if (measurements === void 0) return;
      await this.getComplexCategories(measurements.complex.filter(item => item.metadata));
    });


    this.indicatorsPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      (data) => {
        this.imcValue = cloneDeep(data.measurements.imc.value);
      }
    );
  }

  initValues(): void {
    this.imcValue = 0;
    this.complexCategories = [];
    this.indexClassificationSelected = null;
    this.colorsCategory = ['magenta', 'orange', 'red', 'gold', 'volcano', 'green', 'cyan', 'blue', 'geekblue', 'purple'];
    this.classificationIMC = [
      {
        prefix: 'con',
        value: 'obesidad severa',
        color: '#eb445a',
        range: [3],
        operator: 'gte',
      },
      {
        prefix: 'con',
        value: 'obesidad',
        color: '#ed576b',
        range: [2, 2.999],
        operator: 'in',
      },
      {
        prefix: 'con',
        value: 'sobrepeso',
        color: '#EFB68A',
        range: [1, 1.999],
        operator: 'in',
      },
      {
        prefix: '',
        value: 'normal',
        color: '#74c7ab',
        range: [-0.999, 0.999],
        operator: 'in',
      },
      {
        prefix: 'con',
        value: 'bajo peso o déficit ponderal',
        color: '#EFB68A',
        range: [-1, -1.999],
        operator: 'in',
      },
      {
        prefix: 'con',
        value: 'desnutrición',
        color: '#ed576b',
        range: [-2],
        operator: 'lte',
      },
    ];
    this.classificationCardiovascularRisk = {
      VERY_SLIGHT: '#74c7ab',
      MILD: '#EFB68A',
      MODERATE: '#EFB68A',
      HIGH: '#ed576b',
      VERY_HIGH: '#ed576b',
    };
  }

  async getComplexCategories(complexMeasurementsAvailable: IAnalysisMeasurementResult[]): Promise<void> {
    const patient = await firstValueFrom(this.store
      .selectOnce(PatientState.getSelectedPatient));
    if (patient === void 0) return;

    if (!(patient && patient.user && patient.user.birthday)) {
      this.messageAlertService.createMessage(
        'warning',
        'Para poder realizar un diagnóstico es requerido que el paciente registre su fecha de nacimiento',
        5000
      );
      return;
    }

    const ageMonths = differenceInMonths(
      new Date(),
      new Date(patient.user.birthday)
    );


    if (ageMonths >= 61 && ageMonths <= 216) {
      this.complexCategories = Object.values(
        complexMeasurementsAvailable.reduce((groups, item) => {
          let group = [];
          if (groups[item.category]) {
            group = groups[item.category]?.data;
          }
          group.push(item);
          groups[item.category] = {
            name: item.category,
            data: group,
          };
          return groups;
        }, {})
      );
      this.complexCategories.forEach((item) => {
        item.data.forEach((measurement) => {
          if (measurement && measurement.metadata) {
            this.onShowClassification(measurement);
          }
        });
      });
    } else {
      this.complexCategories = [];
    }

    if (ageMonths > 216) { // preguntar por esta validación
      const withMetadata = cloneDeep(complexMeasurementsAvailable.filter(item => item.metadata));
      this.complexCategories = Object.values(
        withMetadata.reduce((groups, item) => {
          let group = [];
          if (groups[item.category]) {
            group = groups[item.category]?.data;
          }
          group.push(item);
          groups[item.category] = {
            name: item.category,
            data: group,
          };
          return groups;
        }, {})
      );
      this.complexCategories.forEach((item) => {
        item.data.forEach((measurement) => {
          if (measurement && measurement.metadata) {
            this.onShowClassification(measurement);
          }
        });
      });
    }
  }

  onShowClassification(measurement: IAnalysisMeasurementResult): void {
    const value = measurement.metadata.realLow - measurement.metadata.idealLow;

    this.classificationIMC.forEach((item, index) => {
      switch (item.operator) {
        case 'in':
          if (inRange(value, item.range[0], item.range[1])) {
            this.indexClassificationSelected = index;
          }
          break;
        case 'gte':
          if (value >= item.range[0]) {
            this.indexClassificationSelected = index;
          }
          break;
        case 'lte':
          if (value <= item.range[0]) {
            this.indexClassificationSelected = index;
          }
          break;
      }
    });
  }
}
